import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle,
  Eye,
  FilePenLine,
  Hash,
  RefreshCcw,
  Save,
  Search,
  ShieldAlert,
  Trash2,
  X,
} from 'lucide-react';
import { db } from '../../firebase';

const ROOT_COLLECTION = 'tournament_registrations';
const INSCRIPTIONS_SUBCOLLECTION = 'inscriptions';

const TEAM_KEYS = ['teamName', 'team', 'nomEquipe', 'team_name', 'name'];
const EMAIL_KEYS = ['email', 'teamEmail', 'captainEmail', 'mail'];
const PHONE_KEYS = ['phone', 'telephone', 'tel', 'teamPhone'];
const PAYMENT_KEYS = ['paymentStatus', 'paid', 'isPaid', 'payment', 'status'];
const CREATED_AT_KEYS = ['createdAt', 'created_at', 'date', 'registrationDate'];

const isFirestoreTimestamp = (value) => {
  return value && typeof value.toDate === 'function';
};

const isPlainObject = (value) => {
  return (
    value !== null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    !isFirestoreTimestamp(value)
  );
};

const getFirstValue = (data, keys, fallback = '-') => {
  for (const key of keys) {
    if (data?.[key] !== undefined && data?.[key] !== null && data?.[key] !== '') {
      return data[key];
    }
  }

  return fallback;
};

const formatDate = (value) => {
  if (!value) return '-';

  try {
    if (isFirestoreTimestamp(value)) {
      return value.toDate().toLocaleString('fr-BE');
    }

    if (value?.seconds) {
      return new Date(value.seconds * 1000).toLocaleString('fr-BE');
    }

    if (typeof value === 'string' || typeof value === 'number') {
      const date = new Date(value);

      if (!Number.isNaN(date.getTime())) {
        return date.toLocaleString('fr-BE');
      }
    }

    return String(value);
  } catch {
    return String(value);
  }
};

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return '-';

  if (isFirestoreTimestamp(value)) {
    return formatDate(value);
  }

  if (Array.isArray(value)) {
    return value
      .map((entry) => {
        if (typeof entry === 'string') return entry;
        if (typeof entry === 'number') return String(entry);
        if (entry?.name) return entry.name;
        if (entry?.fullName) return entry.fullName;
        if (entry?.displayName) return entry.displayName;
        if (entry?.playerName) return entry.playerName;

        return JSON.stringify(entry);
      })
      .join(', ');
  }

  if (typeof value === 'boolean') {
    return value ? 'Oui' : 'Non';
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
};

const getFieldType = (value) => {
  if (isFirestoreTimestamp(value)) return 'timestamp';
  if (Array.isArray(value)) return 'json';
  if (isPlainObject(value)) return 'json';
  if (typeof value === 'number') return 'number';
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'string') return 'string';
  if (value === null) return 'json';

  return 'string';
};

const toDateTimeLocal = (date) => {
  if (!date) return '';

  const pad = (number) => String(number).padStart(2, '0');

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
};

const valueToEditorValue = (value) => {
  if (isFirestoreTimestamp(value)) {
    return toDateTimeLocal(value.toDate());
  }

  if (Array.isArray(value) || isPlainObject(value) || value === null) {
    return JSON.stringify(value, null, 2);
  }

  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }

  if (value === undefined) {
    return '';
  }

  return String(value);
};

const parseEditorValue = (rawValue, type, fieldName) => {
  if (type === 'number') {
    if (rawValue === '') return null;

    const numberValue = Number(rawValue);

    if (Number.isNaN(numberValue)) {
      throw new Error(`Le champ "${fieldName}" doit être un nombre.`);
    }

    return numberValue;
  }

  if (type === 'boolean') {
    return rawValue === 'true';
  }

  if (type === 'json') {
    try {
      return JSON.parse(rawValue);
    } catch {
      throw new Error(`Le champ "${fieldName}" doit être un JSON valide.`);
    }
  }

  if (type === 'timestamp') {
    if (!rawValue) return null;

    const date = new Date(rawValue);

    if (Number.isNaN(date.getTime())) {
      throw new Error(`Le champ "${fieldName}" doit être une date valide.`);
    }

    return Timestamp.fromDate(date);
  }

  return rawValue;
};

const buildEditableFields = (data) => {
  const fields = {};

  Object.entries(data || {}).forEach(([key, value]) => {
    const type = getFieldType(value);

    fields[key] = {
      type,
      value: valueToEditorValue(value),
    };
  });

  return fields;
};

const getTeamName = (data) => getFirstValue(data, TEAM_KEYS);
const getEmail = (data) => getFirstValue(data, EMAIL_KEYS);
const getPhone = (data) => getFirstValue(data, PHONE_KEYS);
const getPayment = (data) => getFirstValue(data, PAYMENT_KEYS);
const getCreatedAt = (data) => getFirstValue(data, CREATED_AT_KEYS, null);

const getPlayersText = (data) => {
  if (Array.isArray(data?.players)) {
    return formatValue(data.players);
  }

  if (Array.isArray(data?.joueurs)) {
    return formatValue(data.joueurs);
  }

  const players = [
    data?.player1,
    data?.joueur1,
    data?.playerOne,
    data?.player2,
    data?.joueur2,
    data?.playerTwo,
    data?.player3,
    data?.joueur3,
    data?.player4,
    data?.joueur4,
  ].filter(Boolean);

  return players.length > 0 ? players.join(' / ') : '-';
};

const AdminTournamentInscriptions = () => {
  const [availableTournaments, setAvailableTournaments] = useState([]);
  const [tournamentId, setTournamentId] = useState('');
  const [loadedTournamentId, setLoadedTournamentId] = useState('');
  const [tournamentData, setTournamentData] = useState(null);
  const [registrations, setRegistrations] = useState([]);

  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [editableFields, setEditableFields] = useState({});

  const [search, setSearch] = useState('');
  const [loadingTournaments, setLoadingTournaments] = useState(true);
  const [loadingRegistrations, setLoadingRegistrations] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadAvailableTournaments = async () => {
    setLoadingTournaments(true);

    try {
      const snapshot = await getDocs(collection(db, ROOT_COLLECTION));

      const items = snapshot.docs.map((documentSnapshot) => ({
        id: documentSnapshot.id,
        ...documentSnapshot.data(),
      }));

      items.sort((a, b) => {
        const numberA = Number(a.id);
        const numberB = Number(b.id);

        if (!Number.isNaN(numberA) && !Number.isNaN(numberB)) {
          return numberB - numberA;
        }

        return String(b.id).localeCompare(String(a.id));
      });

      setAvailableTournaments(items);
    } catch (err) {
      console.error(err);
      setError('Impossible de charger la liste des tournois.');
    } finally {
      setLoadingTournaments(false);
    }
  };

  useEffect(() => {
    loadAvailableTournaments();
  }, []);

  const loadTournamentRegistrations = async (forcedTournamentId = null) => {
    const cleanTournamentId = String(forcedTournamentId || tournamentId).trim();

    if (!cleanTournamentId) {
      setError('Entre ou sélectionne un numéro de tournoi.');
      return;
    }

    setLoadingRegistrations(true);
    setError('');
    setSuccess('');
    setSelectedRegistration(null);
    setEditableFields({});
    setDeleteTarget(null);
    setDeleteConfirmation('');

    try {
      const tournamentRef = doc(db, ROOT_COLLECTION, cleanTournamentId);
      const tournamentSnapshot = await getDoc(tournamentRef);

      if (!tournamentSnapshot.exists()) {
        setTournamentData(null);
        setRegistrations([]);
        setLoadedTournamentId('');
        setError(`Le tournoi ${cleanTournamentId} n'existe pas dans ${ROOT_COLLECTION}.`);
        return;
      }

      const inscriptionsRef = collection(
        db,
        ROOT_COLLECTION,
        cleanTournamentId,
        INSCRIPTIONS_SUBCOLLECTION
      );

      const inscriptionsSnapshot = await getDocs(inscriptionsRef);

      const items = inscriptionsSnapshot.docs.map((documentSnapshot) => ({
        id: documentSnapshot.id,
        path: documentSnapshot.ref.path,
        data: documentSnapshot.data(),
      }));

      items.sort((a, b) => {
        const dateA = getCreatedAt(a.data);
        const dateB = getCreatedAt(b.data);

        const timeA = isFirestoreTimestamp(dateA)
          ? dateA.toDate().getTime()
          : dateA
            ? new Date(dateA).getTime()
            : 0;

        const timeB = isFirestoreTimestamp(dateB)
          ? dateB.toDate().getTime()
          : dateB
            ? new Date(dateB).getTime()
            : 0;

        if (timeA !== timeB) {
          return timeB - timeA;
        }

        return String(a.id).localeCompare(String(b.id));
      });

      setTournamentId(cleanTournamentId);
      setLoadedTournamentId(cleanTournamentId);
      setTournamentData(tournamentSnapshot.data());
      setRegistrations(items);
      setSuccess(`${items.length} inscription(s) chargée(s) pour le tournoi ${cleanTournamentId}.`);
    } catch (err) {
      console.error(err);
      setError("Impossible de charger les inscriptions du tournoi.");
    } finally {
      setLoadingRegistrations(false);
    }
  };

  const filteredRegistrations = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) return registrations;

    return registrations.filter((registration) => {
      const data = registration.data || {};

      const searchableText = [
        registration.id,
        registration.path,
        getTeamName(data),
        getPlayersText(data),
        getEmail(data),
        getPhone(data),
        getPayment(data),
        JSON.stringify(data),
      ]
        .join(' ')
        .toLowerCase();

      return searchableText.includes(normalizedSearch);
    });
  }, [registrations, search]);

  const openRegistration = (registration) => {
    setSelectedRegistration(registration);
    setEditableFields(buildEditableFields(registration.data));
    setSuccess('');
    setError('');
  };

  const closeRegistration = () => {
    setSelectedRegistration(null);
    setEditableFields({});
  };

  const handleFieldChange = (fieldName, value) => {
    setEditableFields((current) => ({
      ...current,
      [fieldName]: {
        ...current[fieldName],
        value,
      },
    }));
  };

  const handleSave = async () => {
    if (!selectedRegistration || !loadedTournamentId) return;

    const confirmation = window.confirm(
      "Confirmer l'enregistrement ? Tous les champs affichés dans ce document seront mis à jour."
    );

    if (!confirmation) return;

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const payload = {};

      Object.entries(editableFields).forEach(([fieldName, field]) => {
        payload[fieldName] = parseEditorValue(field.value, field.type, fieldName);
      });

      payload.adminUpdatedAt = serverTimestamp();

      const registrationRef = doc(
        db,
        ROOT_COLLECTION,
        loadedTournamentId,
        INSCRIPTIONS_SUBCOLLECTION,
        selectedRegistration.id
      );

      await updateDoc(registrationRef, payload);

      const refreshedSnapshot = await getDoc(registrationRef);

      if (!refreshedSnapshot.exists()) {
        throw new Error("Le document n'existe plus après modification.");
      }

      const refreshedRegistration = {
        id: refreshedSnapshot.id,
        path: refreshedSnapshot.ref.path,
        data: refreshedSnapshot.data(),
      };

      setRegistrations((current) =>
        current.map((registration) =>
          registration.id === selectedRegistration.id ? refreshedRegistration : registration
        )
      );

      setSelectedRegistration(refreshedRegistration);
      setEditableFields(buildEditableFields(refreshedRegistration.data));

      setSuccess('Inscription modifiée avec succès.');
    } catch (err) {
      console.error(err);
      setError(err.message || "Impossible d'enregistrer les modifications.");
    } finally {
      setSaving(false);
    }
  };

  const openDeleteModal = (registration) => {
    setDeleteTarget(registration);
    setDeleteConfirmation('');
    setError('');
    setSuccess('');
  };

  const closeDeleteModal = () => {
    setDeleteTarget(null);
    setDeleteConfirmation('');
  };

  const handleDelete = async () => {
    if (!deleteTarget || !loadedTournamentId) return;

    if (deleteConfirmation !== 'SUPPRIMER') {
      setError('Tu dois écrire SUPPRIMER pour confirmer.');
      return;
    }

    setDeleting(true);
    setError('');
    setSuccess('');

    try {
      const registrationRef = doc(
        db,
        ROOT_COLLECTION,
        loadedTournamentId,
        INSCRIPTIONS_SUBCOLLECTION,
        deleteTarget.id
      );

      await deleteDoc(registrationRef);

      setRegistrations((current) =>
        current.filter((registration) => registration.id !== deleteTarget.id)
      );

      if (selectedRegistration?.id === deleteTarget.id) {
        setSelectedRegistration(null);
        setEditableFields({});
      }

      setSuccess(`Équipe supprimée du tournoi ${loadedTournamentId}.`);
      closeDeleteModal();
    } catch (err) {
      console.error(err);
      setError("Impossible de supprimer cette équipe.");
    } finally {
      setDeleting(false);
    }
  };

  const renderEditorField = (fieldName, field) => {
    if (!field) return null;

    return (
      <div key={fieldName} className="rounded-2xl border border-gray-200 bg-white p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-3">
          <label className="text-sm font-black text-gray-900">
            {fieldName}
          </label>

          <span className="w-fit rounded-full bg-gray-100 px-3 py-1 text-xs font-bold uppercase text-gray-500">
            {field.type}
          </span>
        </div>

        {field.type === 'json' ? (
          <textarea
            value={field.value}
            onChange={(e) => handleFieldChange(fieldName, e.target.value)}
            rows={8}
            className="w-full rounded-2xl border border-gray-300 px-4 py-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#ea4c89]"
          />
        ) : field.type === 'boolean' ? (
          <select
            value={field.value}
            onChange={(e) => handleFieldChange(fieldName, e.target.value)}
            className="w-full rounded-2xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#ea4c89]"
          >
            <option value="true">Oui / true</option>
            <option value="false">Non / false</option>
          </select>
        ) : field.type === 'timestamp' ? (
          <input
            type="datetime-local"
            value={field.value}
            onChange={(e) => handleFieldChange(fieldName, e.target.value)}
            className="w-full rounded-2xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#ea4c89]"
          />
        ) : field.type === 'number' ? (
          <input
            type="number"
            value={field.value}
            onChange={(e) => handleFieldChange(fieldName, e.target.value)}
            className="w-full rounded-2xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#ea4c89]"
          />
        ) : (
          <input
            type="text"
            value={field.value}
            onChange={(e) => handleFieldChange(fieldName, e.target.value)}
            className="w-full rounded-2xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#ea4c89]"
          />
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8">
          <Link
            to="/admin/menu"
            className="mb-4 inline-flex items-center gap-2 text-sm font-bold text-[#4c2f8e]"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour au menu admin
          </Link>

          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-r from-gray-900 to-gray-600 shadow-lg">
                <FilePenLine className="h-7 w-7 text-white" />
              </div>

              <div>
                <h1 className="text-3xl font-black text-[#4c2f8e] md:text-4xl">
                  Gérer les inscriptions tournoi
                </h1>

                <p className="mt-2 text-gray-600">
                  Choisis un tournoi, consulte les équipes, modifie les documents et supprime une équipe si nécessaire.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={loadAvailableTournaments}
              disabled={loadingTournaments}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-gray-200 bg-white px-5 py-3 text-sm font-bold text-gray-700 shadow-sm transition hover:shadow-md disabled:opacity-60"
            >
              <RefreshCcw className="h-4 w-4" />
              Recharger les tournois
            </button>
          </div>
        </div>

        <div className="mb-6 rounded-3xl border border-yellow-200 bg-yellow-50 p-5 text-yellow-900">
          <div className="flex gap-3">
            <ShieldAlert className="mt-0.5 h-6 w-6 flex-shrink-0" />

            <div>
              <h2 className="font-black">Zone sensible</h2>

              <p className="mt-1 text-sm leading-relaxed">
                La suppression retire définitivement le document dans{' '}
                <strong>tournament_registrations/{'{id}'}/inscriptions</strong>.
                Pour éviter une erreur, la suppression demande d’écrire <strong>SUPPRIMER</strong>.
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-5 flex items-center gap-2 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
            <AlertTriangle className="h-5 w-5" />
            {error}
          </div>
        )}

        {success && (
          <div className="mb-5 flex items-center gap-2 rounded-2xl border border-green-200 bg-green-50 p-4 text-sm text-green-800">
            <CheckCircle className="h-5 w-5" />
            {success}
          </div>
        )}

        <div className="mb-6 rounded-3xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
            <div className="lg:col-span-1">
              <label className="mb-2 block text-xs font-black uppercase tracking-wide text-gray-500">
                Numéro du tournoi
              </label>

              <div className="relative">
                <Hash className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />

                <input
                  type="text"
                  value={tournamentId}
                  onChange={(e) => setTournamentId(e.target.value)}
                  placeholder="Exemple : 305"
                  className="w-full rounded-2xl border border-gray-300 py-3 pl-12 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#ea4c89]"
                />
              </div>
            </div>

            <div className="lg:col-span-2">
              <label className="mb-2 block text-xs font-black uppercase tracking-wide text-gray-500">
                Ou choisir dans la liste
              </label>

              <select
                value={tournamentId}
                onChange={(e) => {
                  setTournamentId(e.target.value);

                  if (e.target.value) {
                    loadTournamentRegistrations(e.target.value);
                  }
                }}
                className="w-full rounded-2xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#ea4c89]"
              >
                <option value="">
                  {loadingTournaments ? 'Chargement...' : 'Sélectionner un tournoi'}
                </option>

                {availableTournaments.map((tournament) => (
                  <option key={tournament.id} value={tournament.id}>
                    Tournoi {tournament.id}
                    {tournament.price !== undefined ? ` - ${tournament.price} €` : ''}
                    {tournament.players !== undefined ? ` - ${tournament.players} joueurs` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-end">
              <button
                type="button"
                onClick={() => loadTournamentRegistrations()}
                disabled={loadingRegistrations}
                className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#4c2f8e] px-5 py-3 text-sm font-black text-white transition hover:bg-[#3b2470] disabled:opacity-60"
              >
                <RefreshCcw className="h-4 w-4" />
                {loadingRegistrations ? 'Chargement...' : 'Charger'}
              </button>
            </div>
          </div>

          {loadedTournamentId && tournamentData && (
            <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4">
              <div className="rounded-2xl bg-purple-50 p-4">
                <div className="text-xs font-black uppercase text-purple-700">Tournoi</div>
                <div className="mt-1 text-xl font-black text-purple-900">{loadedTournamentId}</div>
              </div>

              <div className="rounded-2xl bg-gray-50 p-4">
                <div className="text-xs font-black uppercase text-gray-500">Prix</div>
                <div className="mt-1 text-xl font-black text-gray-900">
                  {formatValue(tournamentData.price)} €
                  {tournamentData.tournamentType === 'team_building' && ' / personne'}
                </div>
              </div>

              <div className="rounded-2xl bg-gray-50 p-4">
                <div className="text-xs font-black uppercase text-gray-500">Joueurs</div>
                <div className="mt-1 text-xl font-black text-gray-900">
                  {formatValue(tournamentData.players)}
                </div>
              </div>

              <div className="rounded-2xl bg-gray-50 p-4">
                <div className="text-xs font-black uppercase text-gray-500">ELO</div>
                <div className="mt-1 text-xl font-black text-gray-900">
                  {formatValue(tournamentData.eloMin)} - {formatValue(tournamentData.eloMax)}
                </div>
              </div>
            </div>
          )}
        </div>

        {loadedTournamentId && (
          <div className="mb-5 rounded-3xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />

                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher par équipe, joueur, email, téléphone, ID document..."
                  className="w-full rounded-2xl border border-gray-300 py-3 pl-12 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#ea4c89]"
                />
              </div>

              <div className="text-sm font-bold text-gray-600">
                {filteredRegistrations.length} équipe(s)
                {tournamentData?.tournamentType === 'team_building' && ` — ${filteredRegistrations.length * 2} personnes inscrites`}
              </div>
            </div>
          </div>
        )}

        <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm">
          {!loadedTournamentId ? (
            <div className="p-8 text-center font-semibold text-gray-600">
              Sélectionne d’abord un tournoi.
            </div>
          ) : loadingRegistrations ? (
            <div className="p-8 text-center font-semibold text-gray-600">
              Chargement des équipes...
            </div>
          ) : filteredRegistrations.length === 0 ? (
            <div className="p-8 text-center font-semibold text-gray-600">
              Aucune équipe trouvée dans ce tournoi.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1050px] text-sm">
                <thead className="border-b border-gray-200 bg-gray-50">
                  <tr className="text-left text-gray-600">
                    <th className="p-4 font-black">Doc ID</th>
                    <th className="p-4 font-black">Équipe</th>
                    <th className="p-4 font-black">Joueurs</th>
                    <th className="p-4 font-black">Email</th>
                    <th className="p-4 font-black">Téléphone</th>
                    <th className="p-4 font-black">Paiement</th>
                    <th className="p-4 font-black">Créée le</th>
                    <th className="p-4 text-right font-black">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredRegistrations.map((registration) => {
                    const data = registration.data || {};

                    return (
                      <tr
                        key={registration.id}
                        className="border-b border-gray-100 transition hover:bg-gray-50"
                      >
                        <td className="p-4 font-mono text-xs text-gray-500">
                          {registration.id}
                        </td>

                        <td className="p-4 font-black text-[#4c2f8e]">
                          {formatValue(getTeamName(data))}
                        </td>

                        <td className="p-4 text-gray-700">
                          {getPlayersText(data)}
                        </td>

                        <td className="p-4 text-gray-700">
                          {formatValue(getEmail(data))}
                        </td>

                        <td className="p-4 text-gray-700">
                          {formatValue(getPhone(data))}
                        </td>

                        <td className="p-4 text-gray-700">
                          {formatValue(getPayment(data))}
                        </td>

                        <td className="p-4 text-gray-700">
                          {formatDate(getCreatedAt(data))}
                        </td>

                        <td className="p-4">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => openRegistration(registration)}
                              className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#ea4c89] px-4 py-2 font-bold text-white transition hover:bg-[#d93d7a]"
                            >
                              <Eye className="h-4 w-4" />
                              Voir / modifier
                            </button>

                            <button
                              type="button"
                              onClick={() => openDeleteModal(registration)}
                              className="inline-flex items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-2 font-bold text-white transition hover:bg-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                              Supprimer
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {selectedRegistration && (
          <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 md:items-center">
            <div className="max-h-[92vh] w-full max-w-6xl overflow-y-auto rounded-3xl border border-gray-200 bg-white shadow-2xl">
              <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-gray-200 bg-white p-5">
                <div>
                  <h2 className="text-2xl font-black text-[#4c2f8e]">
                    Document inscription
                  </h2>

                  <p className="mt-1 text-sm text-gray-500">
                    <span className="font-mono">{selectedRegistration.path}</span>
                  </p>
                </div>

                <button
                  type="button"
                  onClick={closeRegistration}
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 transition hover:bg-gray-200"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="grid grid-cols-1 gap-6 p-5 lg:grid-cols-2">
                <div>
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <h3 className="font-black text-gray-900">
                      Modifier tous les champs
                    </h3>

                    <button
                      type="button"
                      onClick={handleSave}
                      disabled={saving}
                      className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#4c2f8e] px-5 py-3 text-sm font-black text-white transition hover:bg-[#3b2470] disabled:opacity-60"
                    >
                      <Save className="h-4 w-4" />
                      {saving ? 'Enregistrement...' : 'Enregistrer'}
                    </button>
                  </div>

                  <div className="space-y-4">
                    {Object.keys(editableFields).length === 0 ? (
                      <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5 text-sm font-semibold text-gray-600">
                        Ce document ne contient aucun champ.
                      </div>
                    ) : (
                      Object.entries(editableFields).map(([fieldName, field]) =>
                        renderEditorField(fieldName, field)
                      )
                    )}
                  </div>

                  <div className="mt-5 rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
                    Pour les champs de type <strong>json</strong>, garde un JSON valide.
                    Exemple pour un tableau : <strong>["Joueur 1", "Joueur 2"]</strong>.
                  </div>
                </div>

                <div>
                  <h3 className="mb-4 font-black text-gray-900">
                    Lecture brute du document
                  </h3>

                  <div className="space-y-3">
                    <div className="rounded-2xl border border-purple-100 bg-purple-50 p-3">
                      <div className="mb-1 text-xs font-black uppercase tracking-wide text-purple-700">
                        chemin firestore
                      </div>

                      <pre className="whitespace-pre-wrap break-words font-mono text-sm text-purple-900">
                        {selectedRegistration.path}
                      </pre>
                    </div>

                    {Object.entries(selectedRegistration.data || {}).map(([key, value]) => (
                      <div
                        key={key}
                        className="rounded-2xl border border-gray-100 bg-gray-50 p-3"
                      >
                        <div className="mb-1 text-xs font-black uppercase tracking-wide text-gray-500">
                          {key}
                        </div>

                        <pre className="whitespace-pre-wrap break-words font-mono text-sm text-gray-800">
                          {Array.isArray(value) || isPlainObject(value)
                            ? JSON.stringify(value, null, 2)
                            : formatValue(value)}
                        </pre>
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={() => openDeleteModal(selectedRegistration)}
                    className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-red-600 px-5 py-3 text-sm font-black text-white transition hover:bg-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                    Supprimer cette équipe
                  </button>
                </div>
              </div>

              <div className="border-t border-gray-200 bg-gray-50 p-5 text-xs text-gray-500">
                Sauvegarde via <strong>updateDoc</strong>. Suppression via <strong>deleteDoc</strong>.
              </div>
            </div>
          </div>
        )}

        {deleteTarget && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-lg rounded-3xl border border-red-200 bg-white p-6 shadow-2xl">
              <div className="mb-4 flex items-start gap-3">
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-red-100">
                  <Trash2 className="h-6 w-6 text-red-700" />
                </div>

                <div>
                  <h2 className="text-2xl font-black text-red-700">
                    Supprimer cette équipe ?
                  </h2>

                  <p className="mt-2 text-sm text-gray-600">
                    Cette action supprimera définitivement le document :
                  </p>

                  <p className="mt-2 rounded-xl bg-gray-100 p-3 font-mono text-xs text-gray-700">
                    {deleteTarget.path}
                  </p>
                </div>
              </div>

              <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                Pour confirmer, écris <strong>SUPPRIMER</strong> ci-dessous.
              </div>

              <input
                type="text"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                placeholder="SUPPRIMER"
                className="mt-4 w-full rounded-2xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
              />

              <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={closeDeleteModal}
                  className="inline-flex flex-1 items-center justify-center rounded-2xl border border-gray-300 bg-white px-5 py-3 text-sm font-black text-gray-700 transition hover:bg-gray-50"
                >
                  Annuler
                </button>

                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleteConfirmation !== 'SUPPRIMER' || deleting}
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-2xl bg-red-600 px-5 py-3 text-sm font-black text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Trash2 className="h-4 w-4" />
                  {deleting ? 'Suppression...' : 'Supprimer définitivement'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTournamentInscriptions;
